/**
 * 공지 배너 — 안 읽은 공지가 있을 때만 한 줄.
 *
 * 모달로 막지 않는다. 공지는 사용자가 하던 일을 끊을 만한 사건이 아니고,
 * 막는 창은 "닫기"를 반사적으로 누르게 만들어 오히려 안 읽힌다
 * (docs/DESIGN-announcements.md §3).
 *
 * 안 읽은 수는 **서버가 센다**. 화면이 목록을 세면 목록이 잘리거나 초안이
 * 섞일 때 조용히 틀린다.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { CampaignOutlined as AnnouncementIcon } from '@mui/icons-material';
import { isAnnounced } from '@/app/services/announcement/announcementService';
import { useAnnouncementStore } from '@/app/stores/announcementStore';

const AnnouncementBanner: React.FC = () => {
  const navigate = useNavigate();
  const items = useAnnouncementStore((s) => s.items);
  const unread = useAnnouncementStore((s) => s.unreadCount);
  const load = useAnnouncementStore((s) => s.load);
  const markReadUpTo = useAnnouncementStore((s) => s.markReadUpTo);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // 공지를 못 받은 것이 화면을 막을 이유는 없다 — 스토어가 실패를 삼킨다
    void load();
  }, [load]);

  // 목록은 번호 내림차순이지만 그에 기대지 않는다
  const announced = items.filter(isAnnounced);
  const highest = announced.reduce((m, a) => Math.max(m, a.number), 0);
  const latest = announced.find((a) => a.number === highest) ?? null;

  const markRead = useCallback(async () => {
    setBusy(true);
    try {
      await markReadUpTo(highest);
    } catch {
      // 실패하면 배너를 그대로 둔다 — 다음에 다시 보인다
    } finally {
      setBusy(false);
    }
  }, [highest, markReadUpTo]);

  if (unread <= 0 || !latest) return null;

  return (
    <Alert
      data-testid="announcement-banner"
      severity="info"
      icon={<AnnouncementIcon fontSize="inherit" />}
      sx={{ mx: 3, mb: 2, alignItems: 'center' }}
      action={
        <Stack direction="row" spacing={1}>
          <Button size="small" color="inherit" onClick={() => navigate(`/announcements/${latest.number}`)}>
            보기
          </Button>
          <Button size="small" color="inherit" disabled={busy} onClick={markRead}>
            읽음으로 표시
          </Button>
        </Stack>
      }
    >
      <Typography variant="body2" component="span">
        {unread > 1 ? `새 공지 ${unread}건 — ` : '새 공지 — '}
        <strong>{latest.title}</strong>
      </Typography>
    </Alert>
  );
};

export default AnnouncementBanner;
